"use client";

import {
  Box,
  Button,
  Collapsible,
  Flex,
  Heading,
  Image,
  Text,
  type BoxProps,
} from "@chakra-ui/react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { LuMinus, LuPackage, LuPlane, LuPlus } from "react-icons/lu";
import NextLink from "next/link";

import { InteractiveButton } from "@components/common/InteractiveButton";
import { Reveal } from "@components/common/Reveal";
import { TypewriterText } from "@components/common/TypewriterText";
import { HowItWorksSection } from "@features/home/HowItWorksSection";
import { SiteFooter } from "@features/home/SiteFooter";
import { SiteTopBar } from "@features/home/SiteTopBar";

const HERO_ROTATING_WORDS = [
  "LOGISTICS",
  "TRACKING",
  "WAREHOUSING",
  "DELIVERY",
] as const;

const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const heroTextStyles = {
  fontSize: { base: "5xl", sm: "6xl", md: "7xl", lg: "8xl", xl: "9xl" },
  fontWeight: "bold" as const,
  color: "fg",
  textTransform: "uppercase" as const,
  letterSpacing: "tight" as const,
  lineHeight: "0.9",
  zIndex: 3,
};

const HeroWord = ({
  children,
  ...positionProps
}: {
  children: React.ReactNode;
  [key: string]: unknown;
}) => (
  <Text position="absolute" {...heroTextStyles} {...positionProps}>
    {children}
  </Text>
);

const HeroSection = () => {
  return (
    <Box position="relative" h={{ base: "auto", md: "90vh" }}>
      {/* Mobile: stacked layout */}
      <Flex
        display={{ base: "flex", md: "none" }}
        flexDirection="column"
        align="center"
        px={6}
        pt={4}
        pb={10}
        gap={0}
      >
        <Text {...heroTextStyles} alignSelf="flex-start">
          SMART
        </Text>
        <Text {...heroTextStyles} alignSelf="center">
          SHIPPING &
        </Text>
        <Text {...heroTextStyles} alignSelf="flex-start">
          <TypewriterText words={HERO_ROTATING_WORDS} />
        </Text>
        <Image
          src="/images/shipping-container.png"
          alt="Shipping container"
          fit="contain"
          w="85%"
          mt={-6}
          mb={2}
          alignSelf="center"
        />
        <Flex flexDirection="column" gap={4} alignSelf="stretch">
          <Text fontSize="md" fontWeight="bold" color="fg">
            Ship from China with confidence. Delivery with transparent timelines
            and regular updates and dedicated support.
          </Text>
          <NextLink href="/track" style={{ display: "contents" }}>
            <InteractiveButton size="sm" w="100%">
              Track your shipment
            </InteractiveButton>
          </NextLink>
          <InteractiveButton
            size="sm"
            variant="surfaceOutline"
            onClick={() => scrollToSection("services")}
          >
            Ship with us
          </InteractiveButton>
        </Flex>
      </Flex>

      {/* Desktop: absolute positioned layout */}
      <Box display={{ base: "none", md: "block" }} position="relative" h="90vh">
        <Flex
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-40%, -50%)"
          w={{ md: "min(55vw, 700px)", lg: "min(50vw, 800px)" }}
          justify="center"
          align="center"
          zIndex={2}
        >
          <Image
            src="/images/shipping-container.png"
            alt="Shipping container"
            fit="contain"
            maxH="65vh"
            w="full"
          />
        </Flex>

        <HeroWord top="10%" left="8%">
          SMART
        </HeroWord>
        <HeroWord top="28%" left="50%" transform="translateX(-50%)">
          SHIPPING &
        </HeroWord>
        <HeroWord bottom="28%" left="8%">
          <TypewriterText words={HERO_ROTATING_WORDS} />
        </HeroWord>
        <Flex
          position="absolute"
          bottom="3%"
          left="8%"
          flexDirection="column"
          gap={4}
          zIndex={3}
        >
          <Text fontSize="lg" fontWeight="bold" color="fg" maxW="md">
            Ship from China with confidence. Delivery with transparent
            timelines, regular updates and dedicated support.
          </Text>
          <Flex gap={3}>
            <NextLink href="/track">
              <InteractiveButton size="sm">
                Track your shipment
              </InteractiveButton>
            </NextLink>
            <InteractiveButton
              size="sm"
              variant="surfaceOutline"
              onClick={() => scrollToSection("services")}
            >
              Ship with us
            </InteractiveButton>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

const SERVICES = [
  {
    id: "shipping",
    label: "Shipping",
    title: "Shipping",
    description:
      "Reliable shipping with transparent delivery timelines and regular status updates from pickup to arrival. ",
    icon: LuPackage,
    href: "/services",
  },
  {
    id: "travel",
    label: "Travel",
    title: "Travel",
    description:
      "Get personalized travel plans to China — from visa support to factory visit scheduling with a dedicated travel coordinator.",
    icon: LuPlane,
    href: "/services/travel",
  },
] as const;

const ServiceCard = ({
  service,
  ...props
}: { service: (typeof SERVICES)[number] } & BoxProps) => {
  const Icon = service.icon;
  const router = useRouter();
  return (
    <Box
      borderRadius="lg"
      borderWidth="1px"
      borderColor="border.light"
      p={8}
      bg="surface.background"
      _hover={{ borderColor: "border.dark", shadow: "md" }}
      transition="all 0.2s"
      cursor="pointer"
      display="flex"
      flexDirection="column"
      {...props}
    >
      <NextLink
        href={service.href}
        style={{ display: "flex", flexDirection: "column", flex: 1 }}
      >
        <Flex justify="space-between" align="flex-start" mb={5}>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color="text.secondary"
            bg="sidebar.hover"
            px={2.5}
            py={1}
            borderRadius="md"
          >
            {service.label}
          </Text>
          <Box as={Icon} boxSize={6} color="fg" opacity={0.7} aria-hidden />
        </Flex>
        <Heading size="md" mb={3} color="fg">
          {service.title}
        </Heading>
        <Text fontSize="md" color="text.secondary" lineHeight="tall" flex={1}>
          {service.description}
        </Text>
        <InteractiveButton
          mt={6}
          size="sm"
          variant="surfaceOutline"
          alignSelf="flex-start"
        >
          Get started
        </InteractiveButton>
      </NextLink>
    </Box>
  );
};

const FAQ_ITEMS = [
  {
    id: "tracking",
    question: "How do I track my shipment?",
    answer:
      "Every shipment gets an ELK tracking number on its shipping label. Enter it on our tracking page to see live status — from warehouse receipt in China to delivery in Ghana.",
  },
  {
    id: "small-shipments",
    question: "Do you handle small shipments?",
    answer: "Yes. We handle both small cargo and full container shipments.",
  },
  {
    id: "ship-from",
    question: "Where in China do you ship from?",
    answer:
      "We ship from Guangzhou, Shenzhen, Yiwu, and other major export hubs.",
  },
  {
    id: "shipping-time",
    question: "How long does shipping take?",
    answer: "Air freight takes 5 to 10 days. Sea freight takes 35 to 50 days.",
  },
  {
    id: "consolidated",
    question: "Do you offer consolidated shipping?",
    answer:
      "Yes. Goods from multiple suppliers can be combined into a single shipment to reduce costs.",
  },
  {
    id: "insurance",
    question: "Is cargo insured?",
    answer:
      "Cargo insurance can be arranged on a case-by-case basis. Contact us for details.",
  },
] as const;

const FaqItem = ({
  item,
  isOpen,
  onOpenChange,
}: {
  item: (typeof FAQ_ITEMS)[number];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) => (
  <Collapsible.Root open={isOpen} onOpenChange={(e) => onOpenChange(e.open)}>
    <Box
      borderBottomWidth="1px"
      borderColor="border.light"
      _last={{ borderBottomWidth: 0 }}
    >
      <Collapsible.Trigger asChild>
        <Flex
          as="button"
          w="full"
          py={5}
          px={5}
          justify="space-between"
          align="center"
          gap={4}
          textAlign="left"
          cursor="pointer"
          _hover={{ bg: "sidebar.hover" }}
          transition="background 0.2s"
        >
          <Text fontWeight="bold" fontSize="md" color="fg">
            {item.question}
          </Text>
          <Box
            as={isOpen ? LuMinus : LuPlus}
            boxSize={5}
            color="fg"
            flexShrink={0}
          />
        </Flex>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <Box px={5} pb={5} pt={0}>
          <Text fontSize="md" color="text.secondary" lineHeight="tall">
            {item.answer}
          </Text>
        </Box>
      </Collapsible.Content>
    </Box>
  </Collapsible.Root>
);

const FaqSection = () => {
  const [openId, setOpenId] = useState<string | null>(FAQ_ITEMS[0].id);

  return (
    <Box
      id="faq"
      as="section"
      py={{ base: 16, md: 20 }}
      px={{ base: 6, md: 12, lg: 16 }}
      bg="surface.background"
    >
      <Box maxW="3xl" mx="auto">
        <Reveal>
          <Flex flexDirection="column" mb={10}>
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color="text.secondary"
              bg="sidebar.hover"
              px={3}
              py={1.5}
              borderRadius="full"
              mb={4}
              w="fit-content"
            >
              FAQ
            </Text>
            <Heading size="xl" color="fg">
              Questions and Answers
            </Heading>
          </Flex>
        </Reveal>

        <Reveal delay={120}>
          <Box
            bg="sidebar.background"
            borderRadius="lg"
            overflow="hidden"
            borderWidth="1px"
            borderColor="border.light"
          >
            {FAQ_ITEMS.map((item) => (
              <FaqItem
                key={item.id}
                item={item}
                isOpen={openId === item.id}
                onOpenChange={(open) => setOpenId(open ? item.id : null)}
              />
            ))}
          </Box>
        </Reveal>
      </Box>
    </Box>
  );
};

const ServicesSection = () => {
  return (
    <Box
      id="services"
      as="section"
      bg="sidebar.background"
      py={{ base: 20, md: 24 }}
      px={{ base: 6, md: 12, lg: 16 }}
    >
      <Box maxW="7xl" mx="auto">
        <Flex flexDirection="column" align="center" textAlign="center" mb={16}>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color="text.secondary"
            bg="surface.background"
            px={3}
            py={1.5}
            borderRadius="full"
            mb={5}
          >
            Logistics Solutions
          </Text>
          <Heading size="xl" mb={4} color="fg">
            Our Services
          </Heading>
          <Text
            fontSize="lg"
            color="text.secondary"
            maxW="2xl"
            lineHeight="tall"
          >
            End-to-end logistics from sourcing to delivery. Clear timelines,
            regular updates, and no surprises.
          </Text>
        </Flex>

        <Flex gap={8} flexDirection={{ base: "column", md: "row" }} mb={12}>
          {SERVICES.map((service, i) => (
            <Reveal key={service.id} delay={i * 120} flex={1}>
              <ServiceCard service={service} h="full" />
            </Reveal>
          ))}
        </Flex>

        <Flex gap={4} justify="center" flexWrap="wrap">
          <a
            href="https://api.whatsapp.com/send?phone=233558239490"
            target="_blank"
            rel="noopener noreferrer"
          >
            <InteractiveButton
              size="sm"
              variant="outline"
              borderColor="border.dark"
              color="fg"
            >
              Contact us
            </InteractiveButton>
          </a>
        </Flex>
      </Box>
    </Box>
  );
};

const HomePage = () => (
  <Box position="relative" minH="100dvh" bg="surface.background">
    <SiteTopBar />
    <HeroSection />
    <HowItWorksSection />
    <ServicesSection />
    <FaqSection />
    <SiteFooter />
  </Box>
);

export default HomePage;
